/* eslint-disable @typescript-eslint/naming-convention */
import DE_Translations from './locales/de.json';
import EN_Translations from './locales/en.json';
import IT_Translations from './locales/it.json';
import FR_Translations from './locales/fr.json';
/* eslint-enable @typescript-eslint/naming-convention */
import * as z from 'zod';

// TODO: this code is quite messy and should be replaced with a proper i18n library

export const systemLanguages = ['de', 'en', 'it', 'fr'] as const;
export type SystemLanguage = (typeof systemLanguages)[number];

function isSystemLanguage(
  maybeSystemLanguage: string,
): maybeSystemLanguage is SystemLanguage {
  return systemLanguages.includes(maybeSystemLanguage as SystemLanguage);
}

// assuming DE contains the complete set of tranlsations
export type Translation = keyof typeof DE_Translations;
type TranslationFile = Record<Translation, string>;

export const systemLanguageLabelMap: Record<SystemLanguage, string> = {
  en: 'English',
  de: 'Deutsch',
  it: 'Italiano',
  fr: 'Français',
};

const languageFileMap: Record<SystemLanguage, Partial<TranslationFile>> = {
  en: EN_Translations,
  de: DE_Translations,
  it: IT_Translations,
  fr: FR_Translations,
};

type Placeholder = { id: string; value: string };
type PlaceholderInput = Placeholder[] | Record<string, string>;

const normalizePlaceholders = (
  placeholders?: PlaceholderInput,
): Placeholder[] => {
  if (!placeholders) {
    return [];
  }

  if (Array.isArray(placeholders)) {
    return placeholders;
  }

  return Object.entries(placeholders).map(([id, value]) => ({
    id,
    value,
  }));
};

export const $t = (
  stringIdentifier: Translation,
  placeholders?: PlaceholderInput,
): string => {
  const language = getUserLanguage();

  const selectedTranslationFile = languageFileMap[language];

  if (!selectedTranslationFile[stringIdentifier]) {
    return stringIdentifier;
  }

  let text = selectedTranslationFile[stringIdentifier];
  for (const placeholder of normalizePlaceholders(placeholders)) {
    text = text.replace(`{{${placeholder.id}}}`, placeholder.value);
  }
  return text;
};

export function getUserLanguage(): SystemLanguage {
  const defaultLangauge = 'de';

  const userSchema = z.object({
    language: z.string().min(1),
  });

  try {
    const user: unknown = JSON.parse(localStorage.getItem('user') ?? '');
    const safeUser = userSchema.safeParse(user);
    if (!safeUser.success) {
      console.warn('Could not find User for translations');
      return defaultLangauge;
    }
    const { language } = safeUser.data;
    if (isSystemLanguage(language)) {
      return language;
    }
    console.warn(
      `Invalid language. Given: "${language}", falling back to language "${defaultLangauge}"`,
    );
    return defaultLangauge;
  } catch {
    // console.warn(
    //   `User not authenticated, falling back to language "${defaultLangauge}"`,
    // );
    return defaultLangauge;
  }
}

export const $pluralize = (
  stringIdentifier0: Translation,
  stringIdentifier1: Translation,
  stringIdentifierMany: Translation,
  number: number,
  // eslint-disable-next-line max-params
) => {
  if (number === 0) {
    return $t(stringIdentifier0, [{ id: 'number', value: String(number) }]);
  } else if (number === 1) {
    return $t(stringIdentifier1, [{ id: 'number', value: String(number) }]);
  } else {
    return $t(stringIdentifierMany, [{ id: 'number', value: String(number) }]);
  }
};
